import { MapPin, DollarSign, Building2, TrendingUp, Home, Layers, Landmark, Scale, Calendar, Users } from 'lucide-react';

const images = [
  'https://images.unsplash.com/photo-1697767217116-465fa27e4521?w=800&q=85',
  'https://images.unsplash.com/photo-1672643405411-10cdbebfc3df?w=800&q=85',
  'https://images.unsplash.com/photo-1606509463400-5ce6440c53e9?w=800&q=85',
  'https://images.unsplash.com/photo-1656501375517-67129e3eed14?w=800&q=85',
  'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800&q=85',
  'https://images.unsplash.com/photo-1730329944265-ab697e1a631c?w=800&q=85',
  'https://images.unsplash.com/photo-1656501382301-9b560230018a?w=800&q=85',
  'https://images.unsplash.com/photo-1557518016-95747c0eef63?w=800&q=85',
  'https://images.unsplash.com/photo-1672626881472-b96d3eb5445e?w=800&q=85',
  'https://images.unsplash.com/photo-1659563159297-6f724e125026?w=800&q=85',
];

export const woolloongabbaArticles = [
  {
    id: 'woolloongabba-property-development-site-guide',
    category: 'Local Guide',
    title: 'Is Your Woolloongabba Property a Development Site? Signs to Look For',
    excerpt: 'Woolloongabba is one of Brisbane\'s most sought-after development precincts. Here\'s how to tell if your property qualifies.',
    date: '2026-04-01',
    readTime: '7 min read',
    image: images[0],
    icon: MapPin,
    metaDescription: 'Is your Woolloongabba property a development site? Learn the signs that your land has development potential in one of Brisbane\'s most active development precincts near Cross River Rail.',
    content: `
## Woolloongabba: Brisbane's Hottest Development Precinct

Woolloongabba sits just 2km south of the Brisbane CBD and is undergoing one of the most significant urban transformations in Australian history. The combination of the new **Cross River Rail underground station**, the **Gabba Olympic redevelopment**, and progressive rezoning has made Woolloongabba the number-one target for developers across South East Queensland.

If you own property in Woolloongabba, there's a strong chance it's worth significantly more as a development site than as a residential home.

## Key Signs Your Woolloongabba Property Has Development Value

### 1. Your Zoning Allows Higher Density

Under the Brisbane City Plan 2014 and the Woolloongabba Neighbourhood Plan, significant parts of the suburb are zoned for:

| Zone | Typical Height | What Can Be Built |
|---|---|---|
| Medium Density Residential | 3-5 storeys | Walk-up apartments, townhouses |
| High Density Residential | 8-15+ storeys | Apartment buildings |
| Centre Zone (near station) | 15-30+ storeys | Mixed-use towers, retail + residential |
| Low-Medium Density | 2-3 storeys | Townhouses, duplexes |

Properties closest to the new Cross River Rail station on Stanley Street and along Logan Road have the highest density allowances.

### 2. You're Within 800m of Cross River Rail

The new underground station at the Gabba is a game-changer. Properties within walking distance of the station are being rezoned to allow dramatically higher densities. If your home is within 400-800 metres of the station entrance, developer interest will be intense.

### 3. Your Block is 500m² or Larger

In Woolloongabba, even a standard 400m² block can attract developer attention in the right zone. Blocks of 600m² or more in medium-to-high density zones are highly sought after.

### 4. You're on a Corner or Have Dual Frontage

Corner blocks along Stanley Street, Vulture Street, Wellington Road, and Logan Road are particularly valuable — dual access allows better building design and often triggers higher density allowances.

> **Pro tip**: If developers have already knocked on your door or sent you letters, that's a strong signal — but never accept an unsolicited offer without independent advice. A specialist agent creates competition that drives significantly higher prices.

## What's Driving Developer Demand in Woolloongabba

- **Cross River Rail station** opening in 2026, creating a new transit-oriented development precinct
- **2032 Olympics** — the Gabba precinct is earmarked for major athletic venues and urban renewal
- **Woolloongabba Priority Development Area** driving rezoning and infrastructure investment
- **Population growth** with sustained demand for inner-city apartment living
- **Proximity to CBD, Mater Hospital, South Bank, and university campuses**

## How to Find Out Your Property's Development Potential

The fastest way to understand what your Woolloongabba property is worth to a developer is to speak with a specialist development site agent. At ACRES, we provide a complimentary, no-obligation assessment covering:

- Your property's current zoning and maximum building height
- Estimated development yield (number of dwellings)
- Recent comparable development site sales in Woolloongabba
- Realistic price expectations from the current developer market

## Frequently Asked Questions

**Q: How much more could my Woolloongabba property be worth as a development site?**
Properties in medium-to-high density zones near the Cross River Rail station can be worth 50-150% more as development sites than as residential homes. The premium depends on zoning, block size, and proximity to the station.

**Q: Do I need to get approval before selling?**
No. Most developers prefer to design their own project. However, a site with an existing Development Approval can attract a 10-20% premium.

**Q: How long will it take to sell?**
Development site sales in Woolloongabba typically take 2-4 months to secure a contract, with settlement periods of 6-18 months depending on conditions.

---

*ACRES provides complimentary development site assessments in Woolloongabba. Call 07 3096 0542 or email daniel@acres.au.*
    `,
    faq: [
      { q: 'Is my Woolloongabba property a development site?', a: 'If your property is zoned Medium Density or higher and is within 800m of the Cross River Rail station, it very likely has significant development value. Corner blocks and lots over 500m² are particularly sought after.' },
      { q: 'How much more is my Woolloongabba land worth to a developer?', a: 'Properties in medium-to-high density zones near Cross River Rail can be worth 50-150% more as development sites. A standard house on 600m² in MDR zoning could attract $1.5M-$2.5M+ from developers.' },
      { q: 'Are developers actively buying in Woolloongabba?', a: 'Yes. Woolloongabba is the most active development site market in Brisbane, driven by Cross River Rail, the 2032 Olympics, and progressive council rezoning.' },
    ],
  },
  {
    id: 'woolloongabba-land-prices-what-developers-pay',
    category: 'Local Guide',
    title: 'What Developers Are Paying for Land in Woolloongabba',
    excerpt: 'Current pricing benchmarks for development sites in Woolloongabba and what drives the numbers.',
    date: '2026-04-28',
    readTime: '6 min read',
    image: images[1],
    icon: DollarSign,
    metaDescription: 'What are developers paying for land in Woolloongabba Brisbane? Current development site pricing, recent sales data, and what drives land values in this Olympic precinct.',
    content: `
## Woolloongabba Development Site Pricing

Woolloongabba has experienced some of the strongest development site price growth in Brisbane over the past three years. The catalyst is clear: Cross River Rail, the Olympics, and sustained inner-city housing demand have combined to create intense competition among developers for available sites.

## Current Price Benchmarks

Development site pricing in Woolloongabba varies significantly based on zoning, size, and proximity to key infrastructure:

| Site Type | Typical Size | Price Range | Price per m² |
|---|---|---|---|
| Standard house (MDR zone) | 400-600m² | $1.2M - $2.0M | $2,500 - $3,500 |
| Large block (MDR zone) | 600-800m² | $1.8M - $3.0M | $2,800 - $4,000 |
| Corner site (HDR zone) | 600-1,000m² | $2.5M - $5.0M+ | $3,500 - $5,500 |
| Amalgamated site (Centre) | 1,000-2,000m² | $5.0M - $15.0M+ | $4,000 - $8,000 |

These figures represent what developers will pay — often 50-150% above standard residential market value for the same property.

## What Drives Price Variation

### Zoning and Height

The single biggest price driver. A 600m² block zoned for 5-storey apartments is worth dramatically more than the same block zoned for 2-storey townhouses. In the Woolloongabba Neighbourhood Plan area, some sites allow 15+ storeys — these command the highest prices.

### Proximity to Cross River Rail

Sites within 400m of the station entrance command a measurable premium. Each additional 100m of distance reduces the development yield and end-product pricing, which flows directly to lower land values.

### Site Configuration

Wide frontage (15m+), flat topography, and corner positions all increase value. Narrow, deep blocks or sloping sites attract fewer buyers and lower prices.

### Existing Approvals

A site with a current DA typically sells for 10-20% more than a comparable unapproved site. The buyer avoids 6-12 months of approval risk and holding costs.

## Recent Market Activity

Developer demand in Woolloongabba remains strong across all site types. National developers, local boutique builders, and build-to-rent operators are all actively competing for sites. The limited remaining supply of developable land in the suburb is putting upward pressure on pricing.

> **Key insight**: Properties that would have sold for $900,000-$1.1M to a home buyer are regularly achieving $1.5M-$2.5M+ when marketed as development sites to the right buyer pool.

## How to Get an Accurate Price for Your Site

Standard residential valuations won't capture development potential. You need a specialist development site appraisal that considers zoning, yield, feasibility, and current developer demand. ACRES provides these at no cost.

## Frequently Asked Questions

**Q: Is the Woolloongabba market overheated?**
Prices reflect genuine development economics — the end-product demand from apartment buyers and renters is very strong. As long as Cross River Rail and Olympic investment continues, developer demand for Woolloongabba sites will remain robust.

**Q: How do I know if a developer's offer is fair?**
Never accept an unsolicited offer without getting an independent appraisal. A specialist agent can test the broader market and create competitive tension that typically achieves 15-30% more than a direct approach.

**Q: Will prices keep rising?**
Long-term fundamentals are strong, but construction cost inflation could moderate price growth. The best time to sell is when your property is well-prepared and the market is competitive.

---

*For a confidential Woolloongabba development site appraisal, contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'What are developers paying for land in Woolloongabba?', a: 'Standard houses on 400-600m² in MDR zones sell for $1.2M-$2.0M. Larger blocks and corner sites in HDR zones command $2.5M-$5.0M+. Amalgamated sites near the station can exceed $15M.' },
      { q: 'How much more will a developer pay vs a home buyer in Woolloongabba?', a: 'Developers typically pay 50-150% above residential value. A house worth $900K-$1.1M to a home buyer can achieve $1.5M-$2.5M+ as a development site.' },
      { q: 'Is the Woolloongabba development site market overheated?', a: 'Prices reflect genuine economics driven by Cross River Rail, Olympic investment, and strong end-buyer demand. Fundamentals remain robust for the medium term.' },
    ],
  },
  {
    id: 'woolloongabba-zoning-guide-what-you-can-build',
    category: 'Local Guide',
    title: 'Woolloongabba Zoning Guide: What You Can Build on Your Block',
    excerpt: 'A plain-English breakdown of Woolloongabba\'s zoning framework and what it means for your property\'s value.',
    date: '2026-04-25',
    readTime: '8 min read',
    image: images[2],
    icon: Scale,
    metaDescription: 'Woolloongabba zoning guide for property owners. Understand your zoning, building heights, and development potential under the Brisbane City Plan and Woolloongabba Neighbourhood Plan.',
    content: `
## Understanding Woolloongabba's Zoning

Woolloongabba's zoning is governed by the **Brisbane City Plan 2014** and the **Woolloongabba Neighbourhood Plan**, which together determine what can be built on every parcel of land in the suburb. For property owners, understanding your zoning is the first step to unlocking your land's true value.

## Residential Zones in Woolloongabba

### Low-Medium Density Residential (LMR)
- **Height**: Up to 9.5m (2-3 storeys)
- **What you can build**: Townhouses, duplexes, triplexes
- **Where in Woolloongabba**: Quieter streets away from main roads, eastern fringe areas
- **Development premium**: Moderate — 30-50% above residential value for suitable sites

### Medium Density Residential (MDR)
- **Height**: Up to 15m (4-5 storeys)
- **What you can build**: Walk-up apartments, larger townhouse complexes
- **Where in Woolloongabba**: Along secondary streets, between main corridors
- **Development premium**: Significant — 50-100% above residential value

### High Density Residential (HDR)
- **Height**: Up to 30m+ (8-15 storeys, varies by location)
- **What you can build**: Apartment buildings, residential towers
- **Where in Woolloongabba**: Along Stanley Street, Logan Road, parts of Vulture Street
- **Development premium**: Very high — 100-200%+ above residential value

## Centre and Mixed-Use Zones

The areas closest to the Gabba cricket ground and the new Cross River Rail station are zoned as **Centre** or **Mixed Use**, allowing the most intensive development:

| Zone | Typical Height | Uses |
|---|---|---|
| Neighbourhood Centre | 4-6 storeys | Retail ground floor + residential above |
| District Centre | 8-15 storeys | Commercial, retail, residential |
| Principal Centre | 15-30+ storeys | Major mixed-use towers |

These zones offer the greatest development potential and the highest land values in Woolloongabba.

## The Woolloongabba Neighbourhood Plan

The Neighbourhood Plan overlays additional provisions on top of the base zoning. Key features for property owners:

- **Transit-oriented development provisions**: Properties near the Cross River Rail station may qualify for additional height and density above the base zone
- **Active frontage requirements**: Ground-floor commercial or retail may be required along key streets
- **Streetscape and character provisions**: Some areas have specific design guidelines
- **Olympic precinct provisions**: The Gabba precinct has special development parameters linked to the 2032 Games

## How to Check Your Zoning

1. Visit [Brisbane City Plan Interactive Mapping](https://cityplan.brisbane.qld.gov.au/)
2. Search your Woolloongabba address
3. Check the **Zoning** layer for your base zone
4. Check **Neighbourhood Plans** for the Woolloongabba-specific provisions
5. Check **Overlays** for flood, heritage, or other constraints

## Important Overlays in Woolloongabba

- **Flood overlay**: Some properties near Norman Creek have flood mapping — this doesn't prevent development but may require elevated ground floors
- **Limited heritage**: Unlike some inner-city suburbs, Woolloongabba has relatively few heritage-listed properties, making development approvals more straightforward

## Frequently Asked Questions

**Q: How do I find out my exact zoning in Woolloongabba?**
Use Brisbane City Plan Interactive Mapping (cityplan.brisbane.qld.gov.au). Search your address and check the Zoning and Neighbourhood Plan layers. For a professional interpretation, contact ACRES for a free assessment.

**Q: Can my zoning change in the future?**
Yes. Council periodically reviews the City Plan. Properties near Cross River Rail and Olympic venues are strong candidates for future upzoning, which would further increase their development value.

**Q: Does higher zoning guarantee I can build to the maximum height?**
Not always. Overlays, neighbourhood plan provisions, and site-specific constraints may limit the achievable height. A town planning assessment will confirm the realistic development potential.

---

*Not sure what your Woolloongabba zoning means? ACRES provides free development site assessments. Call 07 3096 0542.*
    `,
    faq: [
      { q: 'What zoning does my Woolloongabba property have?', a: 'Woolloongabba has a mix of LMR, MDR, HDR, and Centre zones. Properties near Cross River Rail and the Gabba often have the highest density allowances. Check cityplan.brisbane.qld.gov.au for your specific zoning.' },
      { q: 'What can I build on my Woolloongabba block?', a: 'It depends on your zone. LMR allows townhouses (2-3 storeys), MDR allows apartments (4-5 storeys), HDR allows towers (8-15+ storeys), and Centre zones near the station can allow 15-30+ storeys.' },
      { q: 'Will Woolloongabba zoning change because of the Olympics?', a: 'The Woolloongabba Neighbourhood Plan already reflects Olympic-related provisions. Further upzoning is possible as the precinct evolves, potentially increasing property values further.' },
    ],
  },
  {
    id: 'cross-river-rail-woolloongabba-property-values',
    category: 'Local Guide',
    title: 'How Cross River Rail Is Transforming Woolloongabba Property Values',
    excerpt: 'The new underground station is reshaping Woolloongabba\'s property market. Here\'s what it means for landowners.',
    date: '2026-04-22',
    readTime: '7 min read',
    image: images[3],
    icon: TrendingUp,
    metaDescription: 'How Cross River Rail is transforming Woolloongabba property values. Impact of the new underground station on land prices, development demand, and rezoning opportunities.',
    content: `
## The Cross River Rail Effect

Cross River Rail is the largest infrastructure project in Queensland's history — a new 10.2km rail line running under the Brisbane River with four new underground stations. The **Woolloongabba station** is arguably the most transformative of all, sitting directly beneath the Gabba precinct and creating a new transit-oriented development node in the heart of the suburb.

For Woolloongabba property owners, this is a once-in-a-generation event that is fundamentally reshaping land values.

## How Infrastructure Lifts Property Values

Research consistently shows that major transit infrastructure increases surrounding property values:

| Distance from Station | Typical Value Uplift |
|---|---|
| Within 200m | 15-25% above baseline |
| 200-400m | 10-18% above baseline |
| 400-800m | 5-12% above baseline |
| 800m-1.5km | 2-5% above baseline |

For development sites — where value is driven by what can be built, not just what exists — the uplift is even more dramatic because transit proximity increases both the allowable density and the end-product pricing.

## What's Changing in Woolloongabba

### Rezoning and Density Increases
Council has progressively increased height and density allowances around the station precinct. Properties that were zoned for 2-3 storeys a decade ago now allow 5-8+ storeys. Sites immediately adjacent to the station may allow 15-30+ storeys.

### Developer Demand
National and international developers are actively competing for Woolloongabba sites. The station creates a captive audience of future residents who want to live within walking distance of high-frequency public transport.

### End-Buyer Premiums
Apartments within walking distance of Cross River Rail stations are expected to command 10-15% price premiums over comparable products further away. This premium flows directly into higher land values for development sites.

### Rental Market Strength
Inner-city apartments near train stations achieve higher rents and lower vacancy rates. This attracts build-to-rent developers, adding another buyer pool competing for Woolloongabba sites.

## The Olympic Multiplier

Cross River Rail's impact is amplified by the 2032 Olympics. The Gabba precinct — directly above the Woolloongabba station — is being redeveloped as a key Olympic venue. This brings:

- Additional government infrastructure spending
- Accelerated urban renewal and public domain improvements
- National and international attention driving buyer confidence
- Long-term legacy benefits for the precinct

## Timeline of Impact

- **2024-2025**: Station construction completion, early rezoning benefits materialising
- **2025-2028**: Station operational, development applications surge, land values peak growth phase
- **2028-2032**: Olympic preparation accelerates precinct transformation
- **2032+**: Legacy benefits establish Woolloongabba as a premium inner-city precinct

## What This Means for Property Owners

If you own property in Woolloongabba — particularly within 800m of the station — you are sitting on an asset that is appreciating on multiple fronts simultaneously. The question isn't whether your property has development value, but how much — and when is the right time to realise it.

> **Important**: Early movers in infrastructure-driven markets often achieve the best outcomes. As supply of development sites diminishes and competition intensifies, waiting too long can mean competing with fewer remaining opportunities but also potentially higher prices.

## Frequently Asked Questions

**Q: Has Cross River Rail already increased Woolloongabba property values?**
Yes. Land values in Woolloongabba have increased significantly since the project was announced. Properties near the station have seen the largest gains, with development site premiums of 50-150% above residential value now common.

**Q: Will values keep rising after the station opens?**
Historical precedent from other cities suggests that property values continue to rise for 5-10 years after a new station opens, as the precinct matures and development activity intensifies.

**Q: Should I sell now or wait?**
The optimal timing depends on your personal circumstances. Current developer demand is very strong, and selling now locks in significant gains. Waiting could see further appreciation, but also more competition from other sellers. ACRES can advise on the best timing for your situation.

---

*Understand how Cross River Rail affects your Woolloongabba property. Contact ACRES on 07 3096 0542 for a free assessment.*
    `,
    faq: [
      { q: 'How does Cross River Rail affect Woolloongabba property values?', a: 'Properties within 800m of the new station have seen land values increase 50-150% above residential baseline. Transit proximity increases both allowable density and end-product pricing.' },
      { q: 'Will Woolloongabba property values keep rising after the station opens?', a: 'Historical precedent suggests values continue rising 5-10 years after station opening as the precinct matures. The 2032 Olympics provide an additional growth driver.' },
      { q: 'Should I sell my Woolloongabba property now or wait?', a: 'Current developer demand is very strong. Selling now locks in significant gains. The right timing depends on personal circumstances — ACRES can provide tailored advice.' },
    ],
  },
  {
    id: 'selling-woolloongabba-home-to-developer-guide',
    category: 'Local Guide',
    title: 'Selling Your Woolloongabba Home to a Developer: A Complete Guide',
    excerpt: 'Step-by-step process for selling your Woolloongabba property as a development site for maximum value.',
    date: '2026-04-18',
    readTime: '8 min read',
    image: images[4],
    icon: Home,
    metaDescription: 'How to sell your Woolloongabba home to a developer. Complete guide covering preparation, pricing, agent selection, and negotiation to achieve the best development site price.',
    content: `
## Why Woolloongabba Sellers Should Think Development

If your Woolloongabba property sits on land zoned for medium or high density development — and most properties in the suburb do — selling to a developer could unlock 50-150% more value than a standard residential sale.

The process is different from selling a house to a home buyer. Here's what you need to know.

## Step 1: Understand Your Property's Development Potential

Before engaging an agent, get clear on the fundamentals:

- **Check your zoning** at [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
- **Measure your block** — lot size, frontage width, and shape all affect yield
- **Note your position** — corner blocks, proximity to Cross River Rail station, and main road frontage add value
- **Identify constraints** — flood overlays, easements, or heritage listings

### Quick Value Indicators for Woolloongabba

| Block Size | Zone | Estimated Dev Site Value |
|---|---|---|
| 400m² | MDR | $1.2M - $1.6M |
| 600m² | MDR | $1.6M - $2.2M |
| 600m² | HDR | $2.2M - $3.5M |
| 800m² | HDR (corner) | $3.0M - $5.0M+ |

## Step 2: Choose a Specialist Agent

This is the most important decision. A general residential agent will almost certainly undervalue your property and lack access to developer buyers. Look for:

- **Development site transaction experience** in Woolloongabba specifically
- **An active database** of developers currently looking in the area
- **Knowledge of recent comparable sales** and current market conditions
- **A clear marketing strategy** targeting developer buyers

> **Warning**: Unsolicited developer offers are almost always below market value. They're designed to avoid competition. A specialist agent consistently achieves 15-30% more through competitive tension.

## Step 3: Prepare Your Property

Essential preparation for a Woolloongabba development site sale:

- **Boundary survey** ($2,000-$4,000) — every developer needs this
- **Title search** — confirm ownership, easements, and encumbrances
- **Planning summary** — what can be built, maximum height, estimated yield

Optional but valuable:
- **Concept design** from an architect ($5,000-$10,000)
- **Town planning assessment** ($3,000-$5,000)

Don't waste money on house renovations or landscaping — developers will demolish regardless.

## Step 4: Market to Developers

The most effective sale method for Woolloongabba development sites is an **Expressions of Interest (EOI)** campaign:

1. Your agent contacts their developer database directly
2. An information memorandum is prepared with survey, zoning, and planning details
3. Site inspections are conducted with qualified buyers
4. A deadline is set for offers
5. Multiple offers create competitive tension

## Step 5: Negotiate and Close

Negotiate beyond just price — key terms include:
- **Deposit**: 5-10% (higher is better for security)
- **Conditions**: DA conditions, finance, due diligence timeframes
- **Settlement**: 6-18 months depending on conditions
- **Sunset clause**: Deadline for all conditions to be met

## Frequently Asked Questions

**Q: Can I stay in my home while it's being sold to a developer?**
Yes. You can remain in the property throughout the marketing period and the conditional period. You only need to vacate at settlement, which could be 6-18 months after contract exchange.

**Q: How long does the whole process take?**
Typically 2-4 months from listing to contract, then 6-18 months of conditional period (for DA and finance), then 4-8 weeks to settlement. Total: 8-24 months from start to settlement.

**Q: What commission does a development site agent charge?**
Typically 1.5-2.5% of the sale price. Given the significantly higher prices achieved versus a residential sale, this represents excellent value.

---

*Ready to explore your Woolloongabba property's development potential? Call ACRES on 07 3096 0542 for a confidential, no-obligation assessment.*
    `,
    faq: [
      { q: 'How do I sell my Woolloongabba home to a developer?', a: 'Engage a specialist development site agent, prepare your property with a survey and planning summary, market through an EOI campaign to create competitive tension, and negotiate terms as well as price.' },
      { q: 'Can I live in my Woolloongabba home while selling to a developer?', a: 'Yes. You remain in the property through marketing and the conditional period. Settlement is typically 6-18 months after contract, giving you time to plan.' },
      { q: 'How much commission does a development site agent charge in Woolloongabba?', a: 'Typically 1.5-2.5% of the sale price. Given premiums of 50-150% above residential value, the net outcome is significantly better than a standard residential sale.' },
    ],
  },
  {
    id: 'woolloongabba-development-hotspots-where-developers-buy',
    category: 'Local Guide',
    title: 'Woolloongabba Development Hotspots: Where Developers Want to Buy',
    excerpt: 'The specific streets and pockets within Woolloongabba that are attracting the most developer interest.',
    date: '2026-04-15',
    readTime: '6 min read',
    image: images[5],
    icon: Layers,
    metaDescription: 'Woolloongabba development hotspots. Which streets and areas are attracting the most developer demand? Stanley Street, Logan Road, and Cross River Rail precinct analysis.',
    content: `
## Where Developers Are Buying in Woolloongabba

Not all Woolloongabba streets are equal when it comes to development demand. Developers target specific pockets based on zoning, proximity to infrastructure, and end-buyer appeal. Here's where the action is concentrated.

## Hotspot 1: Stanley Street Corridor

Stanley Street is Woolloongabba's main artery and the epicentre of development activity. Properties along and immediately behind Stanley Street benefit from:

- **Centre and HDR zoning** allowing significant height (8-15+ storeys)
- **Direct proximity to the Cross River Rail station**
- **Active commercial frontage** supporting mixed-use development
- **High foot traffic** attractive to retail and hospitality operators

Developer demand along Stanley Street is intense. Sites here command the highest per-square-metre rates in the suburb.

## Hotspot 2: Logan Road Precinct

Logan Road runs south from the Gabba cricket ground and carries significant development potential:

- **MDR to HDR zoning** along much of its length
- **Bus transport corridor** with high-frequency services
- **Larger blocks** on the western side suitable for amalgamation
- **Mixed-use opportunities** at key intersections

Properties on Logan Road between the Gabba and Stones Corner are particularly active for developer acquisitions.

## Hotspot 3: Wellington Road / Gabba Precinct

The streets surrounding the Gabba are ground zero for Olympic-related development:

- **Special precinct provisions** allowing higher densities
- **Government investment** in public domain and infrastructure
- **Strong future demand** from Olympic legacy development
- **Premium positioning** overlooking the stadium precinct

## Hotspot 4: Vulture Street East

The eastern end of Vulture Street, approaching from South Brisbane, carries HDR zoning and benefits from:

- **Dual precinct access** — South Brisbane and Woolloongabba station catchments
- **Established transport** — bus routes and proximity to South Bank station
- **Lifestyle appeal** for end-buyers wanting inner-city living

## Streets with Emerging Potential

| Street / Area | Zone | Why Developers Watch It |
|---|---|---|
| Allen Street | MDR | Quieter location, decent block sizes |
| Hawthorne Street | LMR-MDR | Transition area with upzoning potential |
| Jurgens Street | MDR | Good access, regular blocks |
| Main Street (south end) | MDR | Proximity to new station catchment |
| Boundary Street (east) | MDR | Near commercial precinct |

## What This Means If You Own Property Here

If your property is in any of these hotspot areas, developer interest is either already present or building. The key is to get an independent assessment of your property's development potential before responding to any direct approaches.

> **Key point**: Properties in these hotspots typically sell for 60-150% above standard residential value when marketed correctly to the developer buyer pool.

## Frequently Asked Questions

**Q: My property isn't on a main road — does it still have development potential?**
Yes. Many developers prefer quieter streets for residential projects. If your zoning allows medium or high density, your property has development value regardless of its street profile.

**Q: Are developers only buying large sites?**
No. While larger sites are preferred, developers actively seek standard residential blocks (400-600m²) in the right zones. Site consolidation — buying adjacent properties — is also common.

**Q: How do I know if my street is a development hotspot?**
Contact ACRES for a free assessment. We monitor developer activity across every Woolloongabba street and can tell you exactly where demand is concentrated.

---

*Is your Woolloongabba street a development hotspot? Contact ACRES on 07 3096 0542 for a confidential assessment.*
    `,
    faq: [
      { q: 'Which Woolloongabba streets are developers buying on?', a: 'Stanley Street, Logan Road, Wellington Road, and Vulture Street East are the primary hotspots. Side streets in MDR and HDR zones are also attracting strong developer interest.' },
      { q: 'Do developers buy properties on quiet streets in Woolloongabba?', a: 'Yes. Many developers prefer quieter streets for residential apartment projects. If your zoning allows medium or high density, your property has development value regardless of the street profile.' },
      { q: 'Are developers only buying large blocks in Woolloongabba?', a: 'No. Standard 400-600m² blocks in MDR and HDR zones are actively sought. Developers also pursue site consolidation strategies, buying adjacent properties to create larger sites.' },
    ],
  },
  {
    id: 'future-of-woolloongabba-council-plans-olympics',
    category: 'Local Guide',
    title: 'The Future of Woolloongabba: Council Plans & the Olympic Effect',
    excerpt: 'How council planning changes and the 2032 Olympics are shaping Woolloongabba\'s transformation.',
    date: '2026-04-12',
    readTime: '7 min read',
    image: images[6],
    icon: Landmark,
    metaDescription: 'The future of Woolloongabba Brisbane. How council planning, Cross River Rail, and the 2032 Olympics are transforming the suburb and what it means for property owners.',
    content: `
## Woolloongabba's Transformation

Woolloongabba is undergoing the most significant transformation of any Brisbane suburb. Three converging forces — **Cross River Rail**, the **2032 Olympics**, and **progressive council planning** — are creating a new urban precinct that will bear little resemblance to the Woolloongabba of a decade ago.

For property owners, understanding these changes is essential to making informed decisions about the future of your most valuable asset.

## Cross River Rail: The Catalyst

The new underground station at the Gabba is the primary catalyst for Woolloongabba's transformation:

- **Direct CBD access**: 4-minute underground train to Albert Street station in the heart of the CBD
- **Network connectivity**: Connections to Gold Coast, Sunshine Coast, and western corridor rail lines
- **Transit-oriented development**: Council is progressively increasing height and density allowances within the station catchment
- **Public domain investment**: New plazas, pedestrian connections, and streetscape improvements around station entries

## The 2032 Olympics

The Gabba precinct is central to Brisbane's Olympic plans:

### What's Planned
- **Gabba redevelopment** as the main athletics venue with capacity expansion
- **Athletes' Village** components in the broader precinct
- **Public transport upgrades** connecting Olympic venues
- **Urban renewal** of surrounding streets and public spaces

### Timeline
| Phase | Period | Impact |
|---|---|---|
| Planning & Design | 2024-2027 | Rezoning, site acquisitions, early construction |
| Construction | 2027-2031 | Major building activity, disruption but value creation |
| Games Period | Jul-Aug 2032 | International spotlight on the precinct |
| Legacy Phase | 2032+ | Completed infrastructure, established premium precinct |

## Council Planning Changes

Brisbane City Council has made several planning changes that directly benefit Woolloongabba property owners:

### Woolloongabba Neighbourhood Plan Updates
- Increased height limits near the Cross River Rail station
- New mixed-use provisions along key corridors
- Transit-oriented development incentives
- Streamlined approval pathways for compliant development

### Priority Development Area
Parts of Woolloongabba fall within a Priority Development Area (PDA), which is administered by the state government rather than council. PDAs can allow:
- Greater building heights than the standard city plan
- Faster approval timeframes
- Reduced infrastructure charges
- More flexible land use provisions

## What This Means for Property Owners

### Short Term (2025-2027)
- Developer demand remains strong as early rezoning benefits materialise
- Properties near the station are achieving significant premiums
- Early sellers benefit from strong competition among developers building project pipelines

### Medium Term (2027-2031)
- Construction activity intensifies as Olympic preparation accelerates
- Some disruption but continued strong demand for remaining sites
- Late-stage site acquisitions may see highest prices as supply diminishes

### Long Term (2032+)
- Woolloongabba established as a premium inner-city precinct
- Legacy infrastructure supports sustained property values
- Remaining undeveloped sites become increasingly rare and valuable

## Frequently Asked Questions

**Q: Will the Olympics increase my Woolloongabba property value?**
Yes. Olympic-related investment in infrastructure, public domain, and venue construction directly increases surrounding property values. Historical precedent from Sydney 2000 and London 2012 shows sustained property growth in Olympic precincts.

**Q: Is the council likely to further increase height limits in Woolloongabba?**
Possibly. As the precinct evolves and the Cross River Rail station matures, further density increases are plausible. Properties that are already in higher-density zones will benefit most.

**Q: Should I wait until after the Olympics to sell?**
Not necessarily. Development site demand is strongest during the planning and construction phases when developers are building their project pipelines. Post-Olympics, the development opportunity may be largely built out.

---

*Understand what the future holds for your Woolloongabba property. Contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'How will the 2032 Olympics affect Woolloongabba property values?', a: 'Olympic investment in infrastructure, venues, and urban renewal directly increases property values. Historical data from Sydney and London shows sustained growth in Olympic precincts for a decade or more.' },
      { q: 'Is Woolloongabba being rezoned for higher density?', a: 'Yes. Council has progressively increased height and density allowances, particularly near the Cross River Rail station. Further increases are possible as the precinct matures.' },
      { q: 'Should I sell my Woolloongabba property before or after the Olympics?', a: 'Development site demand is often strongest during planning and construction phases. Post-Olympics, most development opportunities may be built out. Current market conditions are very favourable for sellers.' },
    ],
  },
  {
    id: 'woolloongabba-corner-blocks-large-lots-developer-premiums',
    category: 'Local Guide',
    title: 'Woolloongabba Corner Blocks & Large Lots: Why Developers Pay More',
    excerpt: 'If you own a corner block or large lot in Woolloongabba, here\'s why developers will pay a premium.',
    date: '2026-04-08',
    readTime: '5 min read',
    image: images[7],
    icon: Building2,
    metaDescription: 'Why developers pay more for corner blocks and large lots in Woolloongabba. Premium pricing, design advantages, and what makes your block attractive to development buyers.',
    content: `
## The Corner Block Premium

Corner blocks are the most sought-after development sites in Woolloongabba — and across Brisbane. They offer design, access, and amenity advantages that translate directly into higher end-product values and, therefore, higher land prices.

## Why Developers Pay More for Corners

### Dual Street Frontage
Corner blocks address two streets, allowing:
- **Separate pedestrian and vehicle access** — residents enter from one street, cars from another
- **Better apartment layouts** with more natural light and cross-ventilation
- **More attractive building forms** that engage with both streetscapes
- **Higher density allowances** under some planning provisions

### Design Efficiency
Architects can achieve more efficient floor plates on corner sites:
- More apartments per floor with natural light
- Better common area layouts
- Reduced single-aspect (south-facing) apartments
- More attractive rooftop and communal spaces

### Planning Benefits
Brisbane City Plan often provides additional benefits for corner sites:
- Increased site coverage allowances
- More generous setback provisions
- Additional height in some neighbourhood plans
- Bonus floor area for active street frontages

## Corner Block Premiums in Woolloongabba

| Site Type | Standard Block Premium | Corner Block Premium |
|---|---|---|
| 600m² in MDR | $1.6M - $2.0M | $2.0M - $2.8M |
| 600m² in HDR | $2.2M - $3.0M | $3.0M - $4.5M |
| 800m² in HDR | $3.0M - $4.0M | $4.0M - $6.0M+ |

Corner blocks typically command a **20-40% premium** over comparable mid-block sites in the same zone.

## Large Lots: The Amalgamation Advantage

Large lots (800m²+) in Woolloongabba are rare and highly prized because they allow:
- **Greater development yield** without needing to amalgamate with neighbours
- **More efficient building design** on a single title
- **Faster project commencement** — no amalgamation negotiations
- **Simpler approval processes** — single-site applications

> **If you own a large lot**: You hold a scarce asset in Woolloongabba. As remaining developable land diminishes, large single-title sites become increasingly valuable.

## Site Consolidation Opportunities

If your property is adjacent to another potential development site, you may benefit from **site consolidation** — where a developer purchases multiple adjoining properties to create a larger, more viable development site. In these situations:

- Individual properties can achieve premiums of 10-20% above standalone value
- The developer benefits from increased yield on the larger combined site
- Coordinated sales with neighbours can accelerate the process

## Frequently Asked Questions

**Q: How much more is a corner block worth in Woolloongabba?**
Corner blocks typically sell for 20-40% more than comparable mid-block sites. In HDR zones near Cross River Rail, the premium can be even higher due to the design and density advantages.

**Q: I have a standard 400m² block — is it still worth selling as a development site?**
Yes, if the zoning supports it. Even 400m² blocks in MDR zones attract developer interest in Woolloongabba. Your agent can advise on whether standalone sale or consolidation with a neighbour is the better strategy.

**Q: What if my neighbour doesn't want to sell?**
You can still sell your property independently. A developer may purchase your site and approach your neighbour later, or design a project that works on your site alone.

---

*Own a corner block or large lot in Woolloongabba? Contact ACRES on 07 3096 0542 for a premium site assessment.*
    `,
    faq: [
      { q: 'How much more is a corner block worth in Woolloongabba?', a: 'Corner blocks typically command a 20-40% premium over comparable mid-block sites. A 600m² corner in HDR zoning could be worth $3.0M-$4.5M versus $2.2M-$3.0M for a standard block.' },
      { q: 'Is my standard 400m² Woolloongabba block a development site?', a: 'Yes, if zoned MDR or higher. Even standard blocks attract developer interest in Woolloongabba. Site consolidation with a neighbour can further increase value.' },
      { q: 'Do developers pay more for large lots in Woolloongabba?', a: 'Yes. Large lots (800m²+) are rare and highly prized because they allow greater yield without amalgamation. They command significant premiums over standard-sized blocks.' },
    ],
  },
  {
    id: 'woolloongabba-property-market-2025-development-demand',
    category: 'Local Guide',
    title: 'Woolloongabba Property Market 2026: Development Site Demand & Pricing',
    excerpt: 'Current market conditions for Woolloongabba development sites — who\'s buying, what they\'re paying, and where the market is heading.',
    date: '2026-04-05',
    readTime: '6 min read',
    image: images[8],
    icon: Calendar,
    metaDescription: 'Woolloongabba property market 2025. Current development site demand, pricing trends, buyer activity, and market outlook for sellers in Brisbane\'s hottest development precinct.',
    content: `
## Market Overview: Woolloongabba 2025

Woolloongabba enters 2025 as Brisbane's most active development site market. The convergence of Cross River Rail completion, Olympic preparation, and sustained population growth has created a seller's market with strong competition among developer buyers.

## Current Demand Profile

### Who's Buying
- **National developers**: Major ASX-listed and private developers seeking large-scale apartment sites
- **Local boutique developers**: Brisbane-based operators targeting 20-50 apartment projects
- **Build-to-rent operators**: Institutional investors seeking long-term rental housing sites
- **Student accommodation providers**: Operators targeting proximity to hospitals and universities
- **Joint venture partners**: Developers seeking equity partners for larger precinct-scale projects

### What They Want
- Sites of 600m²+ in MDR, HDR, or Centre zones
- Proximity to Cross River Rail station (within 800m preferred)
- Clean titles with minimal constraints
- Sites with or without existing Development Approval
- Corner blocks and sites with dual frontage

## Pricing Trends

Development site prices in Woolloongabba have seen consistent growth:

| Period | Avg. Price per m² (MDR) | Avg. Price per m² (HDR) |
|---|---|---|
| 2022 | $2,000 - $2,800 | $2,800 - $4,000 |
| 2023 | $2,200 - $3,200 | $3,200 - $4,500 |
| 2024 | $2,500 - $3,500 | $3,500 - $5,500 |
| 2025 (current) | $2,800 - $3,800 | $3,800 - $6,000+ |

Growth has been driven by increasing density allowances, stronger end-buyer demand, and diminishing supply of available sites.

## Supply Dynamics

The number of remaining developable sites in Woolloongabba is declining as more properties are acquired and developed. Key supply indicators:

- **Active listings**: Low — fewer sites publicly marketed than in previous years
- **Off-market activity**: High — many transactions occurring through private agent networks
- **Holdouts**: Some owners waiting for higher prices, further constraining supply
- **Amalgamation**: Developers increasingly pursuing multi-site consolidation strategies

## Market Outlook

### Positive Factors
- Cross River Rail station nearing operational completion
- Olympic investment accelerating
- Population growth driving housing demand
- Limited new supply of development sites

### Watch Factors
- Construction cost inflation affecting developer feasibility
- Interest rate environment impacting buyer finance
- Council approval timeframes extending
- Potential for market correction in end-product prices

## What Should Woolloongabba Sellers Do?

Current market conditions are highly favourable for development site sellers:

1. **Get an independent assessment** of your property's development potential
2. **Choose a specialist agent** with an active Woolloongabba developer database
3. **Prepare thoroughly** — survey, planning summary, concept design
4. **Create competitive tension** through a structured EOI campaign
5. **Negotiate terms as well as price** — settlement, conditions, and deposit all matter

## Frequently Asked Questions

**Q: Is 2025 a good time to sell a development site in Woolloongabba?**
Yes. Developer demand is strong, supply is limited, and infrastructure investment continues. Current conditions are among the most favourable for development site sellers in Woolloongabba's history.

**Q: Are prices still rising?**
Prices have shown consistent growth over the past three years. While the rate of growth may moderate, the structural drivers (Cross River Rail, Olympics, population growth) support continued strong pricing.

**Q: How many developer buyers are active in Woolloongabba?**
ACRES maintains relationships with 50+ active developer buyers currently seeking sites in Woolloongabba and surrounds. Competition for well-located sites remains intense.

---

*For a current market assessment of your Woolloongabba property, contact ACRES on 07 3096 0542.*
    `,
    faq: [
      { q: 'Is 2025 a good time to sell a development site in Woolloongabba?', a: 'Yes. Developer demand is strong, supply is limited, and infrastructure investment continues. Market conditions are among the most favourable for sellers in Woolloongabba history.' },
      { q: 'Are Woolloongabba development site prices still rising?', a: 'Prices have shown consistent growth. While growth rates may moderate, structural drivers like Cross River Rail and the Olympics support continued strong pricing in the medium term.' },
      { q: 'How many developers are buying in Woolloongabba?', a: 'ACRES maintains relationships with 50+ active developer buyers seeking sites in Woolloongabba. Competition for well-located sites remains intense, benefiting sellers.' },
    ],
  },
  {
    id: 'downsizing-woolloongabba-selling-developer-retirement',
    category: 'Local Guide',
    title: 'Downsizing in Woolloongabba: How Selling to a Developer Funds Your Next Chapter',
    excerpt: 'If you\'ve lived in Woolloongabba for years, your home could be worth far more than you think to the right buyer.',
    date: '2026-04-01',
    readTime: '6 min read',
    image: images[9],
    icon: Users,
    metaDescription: 'Downsizing in Woolloongabba by selling to a developer. How long-term homeowners can unlock premium development value to fund retirement and their next chapter.',
    content: `
## Your Woolloongabba Home Could Fund a Comfortable Retirement

If you've owned your Woolloongabba home for 10, 20, or 30+ years, you've likely watched the suburb transform around you. What was once a quiet, affordable inner-city neighbourhood is now one of Brisbane's most sought-after development precincts.

The good news? That transformation means your property could be worth significantly more to a developer than to a traditional home buyer — potentially enough to fund a very comfortable next chapter.

## The Woolloongabba Downsizer Advantage

### Development Premium
Properties in Woolloongabba's medium-to-high density zones regularly sell for 50-150% above standard residential value when marketed as development sites. On a property worth $900,000 to a home buyer, that could mean an additional $450,000-$1,350,000.

### Tax Benefits
If the property has been your principal residence for the entire ownership period, the capital gain is generally **exempt from Capital Gains Tax** under the main residence exemption. This means the development premium could be largely tax-free.

### Government Incentives
The federal government's **Downsizer Contribution** allows individuals aged 55+ to contribute up to $300,000 ($600,000 per couple) from the sale of their home into superannuation, even if it exceeds the normal contribution caps.

## Real-World Example

Consider a typical Woolloongabba scenario:

| Factor | Standard Sale | Developer Sale |
|---|---|---|
| Property | 3-bed Queenslander on 600m² (MDR zone) | Same property |
| Buyer type | Home buyer / renovator | Apartment developer |
| Sale price | $1,000,000 | $1,800,000 |
| Agent commission (2%) | $20,000 | $36,000 |
| Net proceeds | $980,000 | $1,764,000 |
| **Additional value** | — | **$784,000** |

That's an additional $784,000 simply by selling to the right buyer through the right process.

## What Downsizers Can Do With the Premium

- **Purchase a quality downsizer apartment** in a new development (potentially in Woolloongabba itself)
- **Boost superannuation** with a Downsizer Contribution
- **Fund travel, lifestyle, and living expenses** in retirement
- **Help family members** with deposits or education
- **Invest** for ongoing income

## The Emotional Side

Selling a long-held family home is an emotional decision. Many Woolloongabba homeowners have raised families, built communities, and created decades of memories in their properties. At ACRES, we understand this and approach every vendor relationship with sensitivity and respect.

The practical reality, however, is that the suburb's transformation means your property's highest and best use has changed. Realising that value allows you to create new experiences and security in your next chapter.

## How ACRES Supports Downsizers

Our approach for Woolloongabba downsizers includes:

1. **Confidential assessment** — understand your property's development value before making any decisions
2. **No-pressure timeline** — sell when you're ready, not when the market dictates
3. **Extended settlement options** — 6-18 months to plan your next move
4. **Buyer vetting** — we only introduce proven developers with strong track records
5. **Ongoing support** — we can assist with finding your next property if needed

> **First step**: Call ACRES for a confidential, no-obligation assessment. There's no commitment — just information to help you make the best decision for your circumstances.

## Frequently Asked Questions

**Q: Will I have to move out immediately if I sell to a developer?**
No. Development site contracts typically include extended settlement periods of 6-18 months. You can continue living in your home while planning your next move.

**Q: Do I pay tax on the sale if it's my main residence?**
If the property has been your principal residence for the entire ownership period, the capital gain is generally exempt from CGT. Consult a tax advisor for your specific circumstances.

**Q: What if I decide not to sell?**
There's no obligation. Our assessment is free and confidential. Many homeowners simply want to understand their options.

---

*Considering downsizing from your Woolloongabba home? Call ACRES on 07 3096 0542 for a confidential assessment of your property's development potential.*
    `,
    faq: [
      { q: 'How much more could I get selling my Woolloongabba home to a developer?', a: 'Properties in MDR and HDR zones regularly sell for 50-150% above residential value. A home worth $1M to a buyer could achieve $1.5M-$2.5M from a developer.' },
      { q: 'Do I pay CGT if I sell my Woolloongabba home to a developer?', a: 'If the property has been your principal residence for the entire ownership period, the capital gain is generally exempt from CGT under the main residence exemption.' },
      { q: 'How long do I have to move out after selling to a developer?', a: 'Development site contracts typically include extended settlement of 6-18 months. You continue living in your home while planning your next move.' },
    ],
  },
];
